import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Send, Plus, MessageSquare, Sparkles } from "lucide-react";
import api from "../lib/api";
import { SectionLabel } from "../components/Primitives";
import { cn } from "../lib/utils";

const SUGGESTIONS = [
  "What made Timbuktu a center of learning?",
  "Which African states participated in the slave trade, and how?",
  "How did the Haitian Revolution change the Atlantic world?",
  "What is the legacy of the Kingdom of Benin's bronzes?",
];

export default function Tutor() {
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const loadSessions = () => api.get("/tutor/sessions").then((r) => setSessions(r.data)).catch(() => {});
  useEffect(() => { loadSessions(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const openSession = async (sid) => {
    const r = await api.get(`/tutor/sessions/${sid}`);
    setSessionId(sid);
    setMessages(r.data.messages || []);
  };

  const newChat = () => {
    setSessionId(null);
    setMessages([]);
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setBusy(true);
    try {
      const res = await api.post("/tutor/chat", { message: msg, session_id: sessionId });
      setSessionId(res.data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
      loadSessions();
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "I'm unable to respond right now. Please try again." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="tutor-page" className="mx-auto grid max-w-7xl gap-6 px-6 py-10 md:px-12 lg:grid-cols-12">
      {/* SESSIONS SIDEBAR */}
      <aside className="lg:col-span-3">
        <button onClick={newChat} data-testid="new-chat-btn" className="mb-4 flex w-full items-center justify-center gap-2 border border-gold px-4 py-3 text-sm text-gold hover:bg-gold hover:text-ink">
          <Plus className="h-4 w-4" /> New Conversation
        </button>
        <div className="space-y-1">
          {sessions.map((s) => (
            <button
              key={s.session_id}
              onClick={() => openSession(s.session_id)}
              data-testid={`session-${s.session_id}`}
              className={cn(
                "flex w-full items-center gap-2 border-l-2 px-3 py-2 text-left text-sm transition-colors",
                s.session_id === sessionId ? "border-gold bg-ash text-bone" : "border-transparent text-muted hover:text-bone"
              )}
            >
              <MessageSquare className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{s.title}</span>
            </button>
          ))}
        </div>
      </aside>

      {/* CHAT */}
      <section className="flex min-h-[70vh] flex-col border border-edge bg-[#0d0c0b] lg:col-span-9">
        <div className="border-b border-edge px-6 py-4">
          <SectionLabel>AI History Tutor</SectionLabel>
          <p className="text-sm text-muted">Scholarly answers grounded in the historical record. Powered by Claude Sonnet 4.6.</p>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto p-6" data-testid="tutor-messages">
          {messages.length === 0 && (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <Sparkles className="h-8 w-8 text-gold" />
              <h2 className="mt-4 font-serif text-3xl text-bone">Ask anything about African history</h2>
              <p className="mt-2 max-w-md text-sm text-muted">From ancient kingdoms to the diaspora — pose a question or start with one of these.</p>
              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)} data-testid="tutor-suggestion" className="border border-edge px-4 py-3 text-left text-sm text-[#cfc9bd] hover:border-gold/50 hover:text-bone">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}>
              <div className={cn(
                "max-w-[80%] whitespace-pre-wrap px-5 py-3 leading-relaxed",
                m.role === "user" ? "bg-blood text-bone" : "border-l-2 border-gold bg-ash text-[#e8e3d8]"
              )}>
                {m.content}
              </div>
            </motion.div>
          ))}

          {busy && (
            <div className="flex justify-start">
              <div className="flex items-center gap-2 border-l-2 border-gold bg-ash px-5 py-3 text-muted">
                <span className="h-2 w-2 animate-pulse rounded-full bg-gold" />
                <span className="text-sm">Consulting the archive…</span>
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-3 border-t border-edge p-4">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask the tutor a question…"
            data-testid="tutor-input"
            disabled={busy}
            className="flex-1 border border-edge bg-ink px-4 py-3 text-bone placeholder:text-muted focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold"
          />
          <button type="submit" disabled={busy || !input.trim()} data-testid="tutor-send-btn" className="flex items-center gap-2 bg-gold px-5 py-3 font-medium text-ink disabled:opacity-50">
            <Send className="h-4 w-4" />
          </button>
        </form>
      </section>
    </div>
  );
}
